import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  Clock,
  Check,
  Star,
  CalendarDays,
  CalendarCheck,
  BadgeCheck,
  Package,
} from "lucide-react";
import { COURSES, COURSE_DETAILS, BOOKING_POLICY, upcomingDates } from "@/data/site";
import { Heartbeat } from "@/components/site/Heartbeat";
import { Seo } from "@/components/site/Seo";
import { useBooking } from "@/components/site/BookingContext";

export default function CourseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { openBooking } = useBooking();
  const course = COURSES.find((c) => c.id === id);
  const detail = COURSE_DETAILS[id];

  if (!course) {
    return (
      <div className="min-h-[70vh] flex flex-col items-center justify-center gap-4 px-6 text-center">
        <h1 className="font-heading font-bold text-3xl">Corso non trovato</h1>
        <Link to="/" className="text-brand-red font-bold uppercase tracking-widest text-sm">
          ← Torna alla home
        </Link>
      </div>
    );
  }

  return (
    <div data-testid={`course-detail-${id}`}>
      <Seo
        title={`${course.title} — Corso ${course.subtitle} | treffe Frosinone`}
        description={course.description}
        path={`/corsi/${course.id}`}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Course",
          name: course.title,
          description: course.description,
          provider: { "@type": "Organization", name: "treffe — Centro di Formazione 3F", sameAs: "https://3formazione.it/" },
        }}
      />
      {/* Hero */}
      <section className="relative pt-28 pb-16 bg-brand-black text-white overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <img src={course.image} alt="" className="w-full h-full object-cover grayscale" />
          <div className="absolute inset-0 bg-gradient-to-t from-brand-black via-brand-black/70 to-brand-black/40" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 md:px-8">
          <button
            onClick={() => navigate("/")}
            data-testid="course-back"
            className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-white/70 hover:text-white mb-8"
          >
            <ArrowLeft size={16} /> Tutti i corsi
          </button>
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-brand-red mb-4">
            {course.tag} · {course.level}
          </div>
          <h1 className="font-heading font-black uppercase tracking-tighter leading-[0.9] text-5xl md:text-7xl max-w-4xl">
            {course.title}
          </h1>
          <p className="mt-5 text-lg text-white/70 max-w-2xl">{course.subtitle}</p>
          <div className="mt-8 flex flex-wrap items-center gap-8">
            <div>
              <div className="text-xs uppercase tracking-widest text-white/50">Durata</div>
              <div className="font-heading font-bold text-2xl flex items-center gap-2">
                <Clock size={20} /> {course.duration}
              </div>
            </div>
            <button
              type="button"
              onClick={() => openBooking(course.title)}
              data-testid="course-enroll-cta"
              className="bg-brand-red text-white font-bold uppercase tracking-widest text-sm px-8 py-4 hover:bg-white hover:text-brand-black transition-colors"
            >
              Iscriviti con il modulo completo
            </button>
          </div>
        </div>
      </section>

      <Heartbeat height={44} className="bg-brand-black py-4" />

      {/* Body */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-lg text-brand-black leading-relaxed"
            >
              {detail?.long}
            </motion.p>

            <h2 className="font-heading font-bold text-3xl tracking-tight mt-14 mb-6">
              Programma del corso
            </h2>
            <ol className="space-y-3">
              {detail?.program.map((p, i) => (
                <li key={i} className="flex items-start gap-4 border-b border-border pb-3">
                  <span className="font-heading font-black text-brand-red text-xl leading-none w-8 shrink-0">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="text-brand-black">{p}</span>
                </li>
              ))}
            </ol>

            <h2 className="font-heading font-bold text-3xl tracking-tight mt-14 mb-6">
              Obiettivi
            </h2>
            <ul className="grid sm:grid-cols-2 gap-3">
              {detail?.objectives.map((o, i) => (
                <li key={i} className="flex items-start gap-2 text-brand-black">
                  <Check size={18} className="text-brand-red mt-0.5 shrink-0" />
                  {o}
                </li>
              ))}
            </ul>

            {/* Reviews */}
            {detail?.reviews?.length > 0 && (
              <>
                <h2 className="font-heading font-bold text-3xl tracking-tight mt-14 mb-6">
                  Cosa dicono i corsisti
                </h2>
                <div className="grid sm:grid-cols-2 gap-5">
                  {detail.reviews.map((r, i) => (
                    <div
                      key={i}
                      data-testid={`review-${i}`}
                      className="border border-border p-6 bg-white"
                    >
                      <div className="flex gap-1 mb-3">
                        {Array.from({ length: r.rating }).map((_, k) => (
                          <Star key={k} size={16} className="fill-brand-red text-brand-red" />
                        ))}
                      </div>
                      <p className="text-brand-black leading-relaxed mb-3">"{r.text}"</p>
                      <div className="text-sm font-bold text-brand-ink">— {r.name}</div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-4 lg:col-start-9">
            <div className="sticky top-24 space-y-6">
              {detail?.popular?.length > 0 && (
                <div className="border border-border p-6 bg-white">
                  <div className="flex items-center gap-2 text-brand-black font-bold mb-4">
                    <Package size={18} className="text-brand-red" /> I più richiesti
                  </div>
                  <ul className="space-y-2">
                    {detail.popular.map((p) => (
                      <li key={p} className="text-sm text-brand-ink uppercase tracking-wide">{p}</li>
                    ))}
                  </ul>
                </div>
              )}
              {detail?.kit && (
                <div className="border border-border p-6 bg-white">
                  <div className="flex items-center gap-2 text-brand-black font-bold mb-4">
                    <Package size={18} className="text-brand-red" /> Kit didattici
                  </div>
                  <p className="text-sm text-brand-ink">{detail.kit}</p>
                </div>
              )}
              <div className="border border-border p-6 bg-white">
                <div className="flex items-center gap-2 text-brand-black font-bold mb-4">
                  <BadgeCheck size={18} className="text-brand-red" /> Certificazione
                </div>
                <p className="text-sm text-brand-ink">{detail?.certification}</p>
              </div>
              <div className="border border-border p-6 bg-brand-black text-white">
                <div className="flex items-center gap-2 font-bold mb-4">
                  <CalendarDays size={18} className="text-brand-red" /> Prossime date
                </div>
                <ul className="space-y-3">
                  {upcomingDates(id).map((d, i) => (
                    <li key={i} className="flex items-center justify-between border-b border-white/10 pb-2 last:border-0">
                      <div>
                        <div className="font-heading font-bold">{d.date}</div>
                        {d.time && <div className="text-xs text-white/50">{d.time}</div>}
                      </div>
                      <span className="text-[10px] uppercase tracking-widest text-brand-red">{d.seats}</span>
                    </li>
                  ))}
                  {upcomingDates(id).length === 0 && (
                    <li className="text-sm text-white/60">Data da concordare: scrivici e ti proponiamo noi le disponibilità.</li>
                  )}
                </ul>
                <button
                  onClick={() => openBooking(course.title)}
                  data-testid="course-book"
                  className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-brand-red text-white font-bold uppercase tracking-widest text-xs px-5 py-3 hover:bg-white hover:text-brand-black transition-colors"
                >
                  <CalendarCheck size={15} /> Prenota una data
                </button>
              </div>
            </div>
          </aside>
        </div>
      </section>

      <div id="iscrizione" className="bg-brand-black text-white py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-5 md:px-8 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-brand-red mb-3">Iscrizione</div>
            <h2 className="font-heading font-bold text-3xl md:text-5xl tracking-tighter leading-none">
              Stesso modulo, stessi dati, stesso bonifico.
            </h2>
            <p className="mt-4 text-white/65 max-w-2xl">{BOOKING_POLICY}</p>
          </div>
          <button
            type="button"
            onClick={() => openBooking(course.title)}
            data-testid="course-enroll-bottom"
            className="inline-flex items-center justify-center gap-2 bg-brand-red text-white font-bold uppercase tracking-widest text-sm px-8 py-4 hover:bg-white hover:text-brand-black transition-colors shrink-0"
          >
            <CalendarCheck size={17} /> Apri il modulo
          </button>
        </div>
      </div>
    </div>
  );
}
